import type Leaflet from 'leaflet';
import React, {
  FunctionComponent,
  useCallback,
  useRef,
  useContext,
  useState,
  useEffect
} from 'react';
import useStyles from '@front10/helpers/dist/UseStyles';
import { Context } from '@front10/helpers/dist/Context';
import type ReactLeaflet from 'react-leaflet';
import type { Marker } from 'react-leaflet';
import FareItemCard from '../../../items/FareItemCard';
import { FareMarkerData } from '../classes/MarkerData';
import Box from '../../../elements/Box/Box';

export interface FareMarkerProps {
  marker: FareMarkerData;
  onClick?: (marker: FareMarkerData) => void;
  onFocus?: (marker: FareMarkerData) => void;
  onMouseOver: (marker: FareMarkerData | undefined) => void;
  iconSettings?: $TSFixMe;
  iconSettingsStyles?: $TSFixMe;
  popupSettings?: $TSFixMe;
  fareItemCardSettings?: $TSFixMe;
  showTopOffers?: boolean;
  isMapMode?: boolean;
  mapRef?: any;
  markerHover?: any;
  zIndex?: number;
  setZIndex?: (value: number) => void;
}

export type FareMarkerPropsType = FareMarkerProps;

const FareMarker: FunctionComponent<FareMarkerPropsType> = (props: FareMarkerPropsType) => {
  const {
    onClick,
    onFocus,
    onMouseOver,
    marker,
    iconSettings,
    iconSettingsStyles = {},
    popupSettings,
    fareItemCardSettings,
    showTopOffers,
    isMapMode,
    mapRef,
    markerHover,
    zIndex,
    setZIndex,
    ...markerSettings
  } = props;
  const markerRef = useRef<Marker | null>(null);
  const { direction, theme } = useContext(Context);
  const [isOnHover, setIsOnHover] = useState(false);
  const [basicStyle, setBasicStyle] = useState(null);

  const handleMarkerRef = useCallback((ref) => {
    if (ref) {
      ref.leafletElement.openPopup();
      markerRef.current = ref;
    }
  }, []);

  const handlePopupRef = useCallback((ref) => {
    if (ref) {
      markerRef.current?.leafletElement.openPopup();
      ref.leafletElement.bringToBack();
    }
  }, []);

  const handleClick = useCallback(() => {
    if (onClick) {
      onClick(marker);
      if (onFocus) {
        onFocus?.(marker);
      }
    }
  }, [marker, onClick, onFocus]);

  const { Marker, Popup } = require('react-leaflet') as typeof ReactLeaflet;
  const leaflet = require('leaflet') as typeof Leaflet;

  const [iconClassName] = useStyles(iconSettingsStyles, { direction, theme });
  const icon = leaflet.divIcon({
    className: 'custom-div-icon',
    html: `<div class='marker-pin mp-opened ${!isMapMode ? iconClassName : ''}' ></div>`,
    iconSize: [20, 20],
    ...iconSettings
  });

  const fareItemCardProps = {
    ...fareItemCardSettings,
    fareItemCard: Object.assign(
      {
        onMouseEnter: () => onMouseOver(marker),
        onMouseLeave: () => onMouseOver(undefined)
      },
      fareItemCardSettings.fareItemCard
    )
  };

  let fareItemProps = {
    showDate: false,
    showBackgroundImage: false,
    showMiles: false,
    showPill: false,
    showDurationTrip: false,
    fareItemPrice: {
      showPricePromo: false
    },
    showDepartureReturnBeforePrice: true,
    showPrice: true,
    bgColor: 'dark',
    textColor: 'white',
    fareItemOriginDestination: {
      showOrigin: false
    },
    showHorizontalSeparator: false,
    showLastSeen: false,
    showDepartureInfo: false,
    showReturnInfo: false,
    showTravelClass: false,
    showJourneyType: false,
    ...fareItemCardProps
  };

  if (fareItemCardSettings.enableDynamicInfo === true) {
    if (isOnHover) {
      fareItemProps['showOriginDestination'] = true;
    } else {
      fareItemProps['showOriginDestination'] = false;
    }
  }

  useEffect(() => {
    if (markerHover === undefined && isOnHover) {
      onMouseOver(undefined);
      if (mapRef) {
        const element = mapRef?.current?.container?.querySelector('.fareMarker-popup');
        if (element) {
          element.removeAttribute('style');
          element.setAttribute('style', basicStyle);
        }
      }
      setIsOnHover(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basicStyle, mapRef, markerHover, onMouseOver]);

  const adjustCssRule = (css: string, newZIndex: number) =>
    css.includes('z-index') ? css : css + ` z-index:${newZIndex}!important; `;

  return (
    <Marker
      position={marker.location}
      onclick={(e) => {
        handleClick();
        e.target.openPopup();
      }}
      icon={icon}
      ref={handleMarkerRef}
      keyboard={false}
      onmouseover={(event: Leaflet.LeafletMouseEvent) => {
        onMouseOver(marker);
        setIsOnHover(true);
        if (mapRef) {
          if (zIndex && setZIndex) {
            const newZIndex = zIndex + 1;
            setZIndex(newZIndex);
            if (basicStyle === null) {
              const styleData = mapRef?.current?.container
                ?.querySelector('.fareMarker-popup')
                ?.getAttribute('style');
              setBasicStyle(styleData);
              mapRef?.current?.container
                ?.querySelector('.fareMarker-popup')
                ?.setAttribute('style', adjustCssRule(styleData, newZIndex));
            } else {
              const style_ = mapRef?.current?.container
                ?.querySelector('.fareMarker-popup')
                ?.getAttribute('style');
              mapRef?.current?.container
                ?.querySelector('.fareMarker-popup')
                ?.removeAttribute('style');
              mapRef?.current?.container
                ?.querySelector('.fareMarker-popup')
                ?.setAttribute('style', adjustCssRule(style_, newZIndex));
            }
          }
        }
      }}
      onmouseout={() => {
        onMouseOver(undefined);
        if (mapRef) {
          const element = mapRef?.current?.container?.querySelector('.fareMarker-popup');
          if (element) {
            element.removeAttribute('style');
            element.setAttribute('style', basicStyle);
          }
        }
        setIsOnHover(false);
      }}
      {...markerSettings}
    >
      <Popup
        direction="top"
        autoClose={false}
        closeOnClick={false} // TODO: This was null. Why?
        closeOnEscapeKey={false}
        closeButton={false}
        autoPan={false}
        ref={handlePopupRef}
        {...popupSettings}
        className={isOnHover ? 'fareMarker-popup' : ''}
      >
        {showTopOffers === true && (
          <Box onClick={handleClick}>
            <FareItemCard
              data={marker.fare}
              {...fareItemProps}
              ariaHidden={true}
              onMouseEnter={() => {
                setIsOnHover(true);
              }}
              onMouseLeave={() => {
                setIsOnHover(false);
              }}
            />
          </Box>
        )}
      </Popup>
    </Marker>
  );
};

export default React.memo(FareMarker, (prevProps, nextProps) => {
  return (
    prevProps.marker.fare === nextProps.marker.fare &&
    prevProps.showTopOffers === nextProps.showTopOffers &&
    prevProps.markerHover === nextProps.markerHover
  );
});
